/**
 * Finix Flex Script Loader and Utilities
 * 
 * PASS 2: Tokenization foundation
 * 
 * This module handles:
 * - Loading Finix.js SDK (env-aware, cached promise)
 * - Safe initialization with multiple mount support
 * - Form mounting and cleanup
 * 
 * SECURITY:
 * - Raw card data NEVER touches our code
 * - Only token IDs are returned from tokenization
 */

// Finix.js CDN URL (v2)
// Official URL from docs.finix.com: https://js.finix.com/v/2/finix.js
// Note: URL path is /v/2/ NOT /v2/
const FINIX_SDK_URL = 'https://js.finix.com/v/2/finix.js';

// Cached promise for script loading (prevents double-loading)
let scriptLoadPromise = null;
let scriptLoaded = false;

/**
 * Load Finix.js SDK script.
 * 
 * Returns a cached promise - safe to call multiple times.
 * Script is loaded only once regardless of how many components mount.
 * 
 * @returns {Promise<void>} Resolves when Finix global is available
 */
export function loadFinixScript() {
  // Return cached promise if already loading/loaded
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Check if already loaded (e.g., from previous session)
  if (window.Finix && typeof window.Finix.PaymentForm === 'function') {
    scriptLoaded = true;
    scriptLoadPromise = Promise.resolve();
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="finix.js"]');
    if (existingScript) {
      // Wait for it to load
      if (window.Finix) {
        scriptLoaded = true;
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => {
        scriptLoaded = true;
        resolve();
      });
      existingScript.addEventListener('error', (event) => {
        const errorUrl = existingScript.src;
        reject(new Error(`Failed to load Finix.js from existing script tag. URL: ${errorUrl}`));
      });
      return;
    }

    // Create and append script
    const script = document.createElement('script');
    script.src = FINIX_SDK_URL;
    script.async = true;
    script.id = 'finix-js-sdk';

    script.onload = () => {
      if (window.Finix && typeof window.Finix.PaymentForm === 'function') {
        scriptLoaded = true;
        console.log('[FinixFlex] SDK loaded successfully from:', FINIX_SDK_URL);
        resolve();
      } else {
        reject(new Error(`Finix.js loaded from ${FINIX_SDK_URL} but Finix.PaymentForm not available`));
      }
    };

    script.onerror = (event) => {
      scriptLoadPromise = null; // Reset so retry is possible
      // Surface exact URL and potential error reason (no secrets exposed)
      const errorDetails = `URL attempted: ${FINIX_SDK_URL}. Check network tab for details.`;
      console.error('[FinixFlex] Script load failed:', errorDetails);
      reject(new Error(`Failed to load Finix.js SDK. ${errorDetails}`));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

/**
 * Check if Finix SDK is loaded and ready.
 * 
 * @returns {boolean}
 */
export function isFinixReady() {
  return scriptLoaded && window.Finix && typeof window.Finix.PaymentForm === 'function';
}

/**
 * Initialize Finix Payment Form (single hosted iframe with internal submit button).
 * 
 * @param {string} elementId - ID of container element
 * @param {Object} config - Configuration
 * @param {string} config.environment - 'sandbox' or 'live'
 * @param {string} config.applicationId - Finix application ID
 * @param {Function} config.onTokenize - Callback when tokenization succeeds (tokenId, card, address)
 * @param {Function} config.onError - Callback when tokenization fails (message, code)
 * @param {Function} config.onUpdate - Callback for form state updates (state, binInformation, hasErrors)
 * @returns {Object|null} Form instance or null if failed
 */
export function initializeFinixForm(elementId, config) {
  console.log('[FinixFlex] initializeFinixForm called');
  
  if (!isFinixReady()) {
    console.error('[FinixFlex] SDK not loaded. Call loadFinixScript() first.');
    return null;
  }
  console.log('[FinixFlex] SDK is ready');

  const {
    environment = 'sandbox',
    applicationId,
    onTokenize,
    onError,
    onUpdate,
  } = config;

  if (!applicationId) {
    console.error('[FinixFlex] applicationId is required');
    return null;
  }
  console.log('[FinixFlex] Application ID provided:', applicationId.substring(0, 6) + '...');

  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`[FinixFlex] Element #${elementId} not found`);
    return null;
  }
  
  // Check element visibility and dimensions
  const rect = element.getBoundingClientRect();
  console.log(`[FinixFlex] Container found: id=${elementId}, visible=${!element.classList.contains('hidden')}, width=${rect.width}, height=${rect.height}`);

  // Note: Container should be empty - React should not render children inside it
  // to avoid removeChild conflicts during Finix initialization

  // FINIX PAYMENTFORM CONFIGURATION
  // ================================
  // Using single hosted iframe with Finix's internal submit button.
  // onSubmit callback handles tokenization response.
  // External submit buttons are NOT used - iframe submit is the single source of truth.
  const formOptions = {
    // Core: card payments only
    paymentMethods: ['card'],
    
    // No address collection in iframe
    showAddress: false,
    
    // Callback when form loads
    onLoad: () => {
      console.log('[FinixFlex] Form loaded successfully');
    },
    
    // Form state update callback
    onUpdate: (state, binInformation, hasErrors) => {
      if (onUpdate) {
        onUpdate({ state, binInformation, hasErrors, isValid: !hasErrors });
      }
    },
    
    // Tokenization response callback - triggered by Finix's internal submit button
    onSubmit: (error, response) => {
      const timestamp = new Date().toISOString();
      
      if (error) {
        // TELEMETRY: payment_token_failure
        console.error('[FinixFlex] payment_token_failure', {
          timestamp,
          error_code: error.code || 'UNKNOWN',
          error_message: error.message || 'Tokenization failed',
        });
        
        if (onError) {
          onError({
            message: error.message || 'Card validation failed',
            code: error.code || 'TOKENIZATION_ERROR',
          });
        }
        return;
      }
      
      const tokenData = response?.data || {};
      const tokenId = tokenData.id;
      
      if (tokenId) {
        // TELEMETRY: payment_token_success
        console.log('[FinixFlex] payment_token_success', {
          timestamp,
          token_prefix: tokenId.substring(0, 8),
          instrument_type: tokenData.instrument_type,
        });
        
        if (onTokenize) {
          // Return only safe data - NO raw card numbers
          onTokenize({
            tokenId,
            instrumentType: tokenData.instrument_type,
            currency: tokenData.currency,
            fingerprint: tokenData.fingerprint,
            card: tokenData.card || {},
            address: tokenData.address || {},
          });
        }
      } else {
        // TELEMETRY: payment_token_failure (no token ID)
        console.error('[FinixFlex] payment_token_failure', {
          timestamp,
          error_code: 'NO_TOKEN_ID',
          error_message: 'No token ID in response',
        });
        
        if (onError) {
          onError({
            message: 'Unable to process card. Please try again.',
            code: 'NO_TOKEN_ID',
          });
        }
      }
    },
  };

  try {
    // Initialize Finix Payment Form
    // Parameters: elementId, environment, applicationId, options
    console.log('[FinixFlex] Calling Finix.PaymentForm()...');
    console.log('[FinixFlex] Params: element=' + elementId + ', env=' + environment);
    
    const form = window.Finix.PaymentForm(
      elementId,
      environment,
      applicationId,
      formOptions
    );
    
    console.log('[FinixFlex] Finix.PaymentForm() returned:', form ? 'form object' : 'null/undefined');
    console.log(`[FinixFlex] Form initialized successfully: env=${environment}, element=${elementId}`);
    return form;
  } catch (error) {
    console.error('[FinixFlex] Error initializing form:', error);
    console.error('[FinixFlex] Error details:', error.message);
    if (onError) {
      onError({
        message: error.message || 'Failed to initialize payment form',
        code: 'INIT_ERROR',
      });
    }
    return null;
  }
}

/**
 * Clean up Finix form from an element.
 * 
 * @param {string} elementId - ID of container element
 */
export function cleanupFinixForm(elementId) {
  try {
    const element = document.getElementById(elementId);
    if (element && element.parentNode) {
      // Clear children safely
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }
      console.log(`[FinixFlex] Form cleaned up: ${elementId}`);
    }
  } catch (e) {
    // Ignore cleanup errors - element may have been removed by React
    console.log(`[FinixFlex] Cleanup skipped for ${elementId}:`, e.message);
  }
}

/**
 * Get test card numbers for sandbox testing.
 * 
 * These are Finix's documented test cards.
 * ONLY for use in sandbox environment.
 */
export const FINIX_TEST_CARDS = {
  visa: {
    number: '4111111111111111',
    brand: 'VISA',
    description: 'Visa (Success)',
  },
  mastercard: {
    number: '5555555555554444',
    brand: 'MASTERCARD',
    description: 'Mastercard (Success)',
  },
  amex: {
    number: '378282246310005',
    brand: 'AMERICAN_EXPRESS',
    description: 'American Express (Success)',
  },
  discover: {
    number: '6011111111111117',
    brand: 'DISCOVER',
    description: 'Discover (Success)',
  },
  decline: {
    number: '4000000000000002',
    brand: 'VISA',
    description: 'Visa (Decline)',
  },
};

export default {
  loadFinixScript,
  isFinixReady,
  initializeFinixForm,
  cleanupFinixForm,
  FINIX_TEST_CARDS,
};
